type UnicodeRange = [number, number] | number;

interface FontMetrics {
    lineHeightRatio: number;
    scaleFactor: number;
}

interface FontCoverageData {
    fonts: string[];
    coverage: Record<string, UnicodeRange[]>;
    metrics: Record<string, FontMetrics>;
}

export type FontCoverage = { font: string; coverage: number }

const DEFAULT_FONT_FAMILY = 'noto sans';

let coverageData: FontCoverageData | null = null;
let coveragePromise: Promise<FontCoverageData> | null = null;

async function loadCoverageData(): Promise<FontCoverageData> {
    if (coverageData) return coverageData;
    if (coveragePromise) return coveragePromise;

    coveragePromise = import('@/data/fontGlyphCoverage.json')
        .then(module => module.default as unknown as FontCoverageData);

    coverageData = await coveragePromise;
    return coverageData;
}

const loadedFonts = new Set<string>();
const pendingFonts = new Map<string, Promise<void>>();

function isCharInRanges(charCode: number, ranges: UnicodeRange[]): boolean {
    let left = 0;
    let right = ranges.length - 1;

    while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const range = ranges[mid];

        if (typeof range === 'number') {
            if (charCode === range) return true;
            if (charCode < range) {
                right = mid - 1;
            } else {
                left = mid + 1;
            }
        } else {
            const [start, end] = range;
            if (charCode >= start && charCode <= end) return true;
            if (charCode < start) {
                right = mid - 1;
            } else {
                left = mid + 1;
            }
        }
    }

    return false;
}

function getBaseFontFamily(fontFamily: string): string {
    const parts = fontFamily.split(' ');
    if (parts[0] === 'Noto' && parts.length > 1) {
        return `${parts[0]} ${parts[1]}`;
    }
    return parts[0];
}

function deduplicateFontFamilies(fontCoverages: FontCoverage[]): FontCoverage[] {
    const groups = new Map<string, FontCoverage[]>();

    for (const fc of fontCoverages) {
        const base = getBaseFontFamily(fc.font);
        if (!groups.has(base)) {
            groups.set(base, []);
        }
        groups.get(base)!.push(fc);
    }

    return Array.from(groups.values()).map(group =>
        group.reduce((best, current) => {
            if (current.coverage > best.coverage) return current;
            if (current.coverage < best.coverage) return best;
            return current.font.length < best.font.length ? current : best;
        })
    );
}

export async function getSupportedFonts(
    characters: Set<string>,
    coverageThreshold: number = 0.85
): Promise<FontCoverage[]> {
    const data = await loadCoverageData();

    const allCharCodes = Array.from(characters)
        .map(char => char.codePointAt(0))
        .filter(code => code !== undefined);

    const asciiCount = allCharCodes.filter(code => code <= 127).length;
    const nonAsciiCodes = allCharCodes.filter(code => code > 127);
    const fontCoverages: FontCoverage[] = [];

    for (const fontFamily of data.fonts) {
        const ranges = data.coverage[fontFamily];

        if (!ranges) {
            continue;
        }

        let supportedCount = asciiCount;
        for (const charCode of nonAsciiCodes) {
            if (isCharInRanges(charCode, ranges)) {
                supportedCount++;
            }
        }

        const coverage = allCharCodes.length ? supportedCount / allCharCodes.length : 1;
        fontCoverages.push({ font: fontFamily, coverage });
    }

    fontCoverages.sort((a, b) => b.coverage - a.coverage);

    const filteredFonts = fontCoverages.filter(fc => fc.coverage >= coverageThreshold);

    if (filteredFonts.length === 0 && fontCoverages.length > 0) {
        return [fontCoverages[0]];
    }

    return deduplicateFontFamilies(filteredFonts);
}

export function getDefaultFont(fonts: string[]): string {
    const result = fonts.find(f => f.toLowerCase().includes(DEFAULT_FONT_FAMILY));
    return result ?? fonts[0];
}

export async function loadFont(fontFamily: string, waitForLoad = true): Promise<void> {
    if (loadedFonts.has(fontFamily)) {
        return;
    }

    if (pendingFonts.has(fontFamily)) {
        return waitForLoad ? pendingFonts.get(fontFamily) : undefined;
    }

    if (fontFamily === 'Arial' || fontFamily === 'serif' || fontFamily === 'sans-serif' || fontFamily === 'monospace') {
        loadedFonts.add(fontFamily);
        return;
    }

    const loadPromise = (async () => {
        try {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:ital,wght@0,400;0,700;1,400;1,700&display=swap`;

            document.head.appendChild(link);

            if (waitForLoad) {
                const timeout = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Font load timeout')), 5000)
                );

                await Promise.race([
                    document.fonts.load(`16px "${fontFamily}"`),
                    timeout
                ]);

                // Wait one tick for the Canvas/Layout engine to update internal font maps.
                await new Promise(resolve => {
                    requestAnimationFrame(() => setTimeout(resolve, 0));
                });

            }

            loadedFonts.add(fontFamily);
            pendingFonts.delete(fontFamily);
        } catch (error) {
            console.warn(`Failed to load font "${fontFamily}", will use fallback:`, error);
            pendingFonts.delete(fontFamily);
        }
    })();

    pendingFonts.set(fontFamily, loadPromise);

    return waitForLoad ? loadPromise : undefined;
}

export function preloadFonts(fontFamilies: string[]): void {
    fontFamilies.forEach(font => {
        loadFont(font, false).catch(() => {});
    });
}

export async function getLineHeightRatio(fontFamily: string): Promise<number> {
    const data = await loadCoverageData();
    const metrics = data.metrics[fontFamily];

    if (metrics) {
        return metrics.lineHeightRatio;
    }

    return 1.15;
}

export async function getAssToCssScaleFactor(fontFamily: string): Promise<number> {
    const data = await loadCoverageData();
    const metrics = data.metrics[fontFamily];

    if (metrics) {
        return metrics.scaleFactor;
    }

    return 1.0;
}
